/**
 * A process as the chat's canvas shows it (Paper CH12 and CH12b): the studio's steps and their eight
 * answers, a draft being edited, and what Abacor has proposed against it. There is no process
 * service yet, so these are the web app's own shapes and `sample.ts` fills them.
 */

/** The eight questions a step answers, in the order the step panel asks them. */
export const ANSWER_KEYS = [
  "doneWhen",
  "each",
  "onlyWhen",
  "after",
  "who",
  "uses",
  "limits",
  "ifItCant",
] as const;
export type AnswerKey = (typeof ANSWER_KEYS)[number];

/** All of them but `after`, which is drawn from what a step depends on and is not written. */
export type WritableKey = Exclude<AnswerKey, "after">;

export const ANSWER_LABEL: Record<AnswerKey, string> = {
  doneWhen: "DONE WHEN",
  each: "EACH",
  onlyWhen: "ONLY WHEN",
  after: "AFTER",
  who: "WHO",
  uses: "USES",
  limits: "LIMITS",
  ifItCant: "IF IT CAN'T",
};

export type Answers = Partial<Record<WritableKey, string>>;

export type Step = {
  id: string;
  title: string;
  /** Groups are the only nesting. */
  groupId?: string;
  /** What this step waits for: the ids of steps it comes after. Empty is a first step. */
  after: readonly string[];
  /** Why it comes after, in a few words ("it uses the Xero contact"). Shown in the step panel. */
  afterNote?: string;
  /** The answers written on this step. The rest are inherited from its group and the process. */
  answers: Answers;
  /** Where the step came from: "Maya's walkthrough, 12:40", "This chat". */
  from?: string;
};

export type Group = {
  id: string;
  title: string;
  after: readonly string[];
  answers: Answers;
};

export type Process = {
  id: string;
  name: string;
  /** The teammate who runs it. */
  owner: string;
  /** The version that is live, or null for a process that has never been released. */
  liveVersion: number | null;
  /** The answers every step inherits unless it or its group says otherwise. */
  defaults: Record<WritableKey, string>;
  groups: readonly Group[];
  /** The draft's steps as they are now, in order. */
  steps: readonly Step[];
};

/** Who made a change: a person, or Abacor through a proposal a person added. */
export type Author = "me" | "abacor";

export type Change = {
  id: string;
  at: Date;
  by: Author;
  kind: "answer" | "step";
  stepId: string;
  /** For an answer: which one, and what was written on the step before (null: nothing, inherited). */
  key?: WritableKey;
  before?: string | null;
  after?: string;
};

export type Proposal = {
  id: string;
  at: Date;
  kind: "answer" | "new-step";
  /** Where it came from: "This chat". */
  from: string;
  status: "pending" | "added" | "dismissed";
  /** An answer proposal: the step and answer it changes, the value it assumes is there, and its own. */
  stepId?: string;
  key?: WritableKey;
  baseline?: string;
  value?: string;
  /** A new-step proposal: the step and the one it follows. A person may edit it before adding it. */
  step?: Step;
  afterStepId?: string;
};

export type Draft = {
  /** The revision being edited: r4 is the draft that becomes v4. */
  rev: number;
  changes: readonly Change[];
  proposals: readonly Proposal[];
};

export type ProcessState = { process: Process; draft: Draft };

/** Why a change was not made, in words a person can read. */
export type Refusal = { refusal: string };
